import { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import { Badge } from 'components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from 'components/ui/dialog';
import { ScrollArea } from 'components/ui/scroll-area';
import { Switch } from 'components/ui/switch';
import { Checkbox } from 'components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Users, 
  Building2, 
  BookOpen, 
  ClipboardList,
  Plus,
  Trash2,
  Edit,
  Eye,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  Send,
  Brain,
  Gamepad2
} from 'lucide-react';



const AdminPanel = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [evaluations, setEvaluations] = useState({ answer_evaluations: [], roleplay_evaluations: [] });
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  // Form states
  const [newDept, setNewDept] = useState({ name: '', description: '' });
  const [newModule, setNewModule] = useState({
    title: '',
    description: '',
    content: '',
    video_url: '',
    video_type: 'youtube',
    duration_minutes: 30,
    category: '',
    difficulty: 'beginner',
    skills: [],
    questions: [],
    roleplay_scenario: null,
    image_url: ''
  });
  const [newAssignment, setNewAssignment] = useState({
    module_id: '',
    assign_to: 'all',
    department_ids: [],
    user_ids: [],
    due_date: '',
    mandatory: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, deptsRes, usersRes, modulesRes, assignRes, evalsRes] = await Promise.all([
        api.get('/admin/stats'),
       api.get('/admin/departments'),
       api.get('/admin/users'),
       api.get('/admin/modules'),
       api.get('/admin/assignments'),
       api.get('/admin/evaluations')
      ]);
      
      setStats(statsRes.data);
      setDepartments(deptsRes.data);
      setUsers(usersRes.data);
      setModules(modulesRes.data);
      setAssignments(assignRes.data);
      setEvaluations(evalsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Error al cargar datos de administración');
    } finally {
      setLoading(false);
    }
  };

  // Department functions
  const createDepartment = async () => {
    try {
      await api.post('/admin/departments', newDept);
      toast.success('Departamento creado');
      setShowDeptModal(false);
      setNewDept({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear departamento');
    }
  };

  const deleteDepartment = async (deptId) => {
    if (!window.confirm('¿Eliminar este departamento?')) return;
    try {
      await api.delete(`/admin/departments/${deptId}`);
      toast.success('Departamento eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar departamento');
    }
  };

  // Module functions
  const createModule = async () => {
    try {
      const moduleData = {
        ...newModule,
        skills: newModule.skills.filter(s => s.trim()),
        questions: newModule.questions.filter(q => q.question && q.correct_answer)
      };
      
      if (selectedModule) {
         await api.put(`/admin/modules/${selectedModule.id}`, moduleData);
        toast.success('Módulo actualizado');
      } else {
         await api.post('/admin/modules', moduleData);        
         toast.success('Módulo creado');
      }
      
      setShowModuleModal(false);
      resetModuleForm();
      fetchData();
    } catch (error) {
      toast.error('Error al guardar módulo');
    }
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm('¿Eliminar este módulo y todas sus asignaciones?')) return;
    try {
      await api.delete(`/admin/modules/${moduleId}`);
      toast.success('Módulo eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar módulo');
    }
  };

  const editModule = (module) => {
    setSelectedModule(module);
    setNewModule({
      title: module.title,
      description: module.description,
      content: module.content,
      video_url: module.video_url || '',
      video_type: module.video_type || 'youtube',
      duration_minutes: module.duration_minutes,
      category: module.category,
      difficulty: module.difficulty,
      skills: module.skills || [],
      questions: module.questions || [],
      roleplay_scenario: module.roleplay_scenario,
      image_url: module.image_url || ''
    });
    setShowModuleModal(true);
  };

  const resetModuleForm = () => {
    setSelectedModule(null);
    setNewModule({
      title: '',
      description: '',
      content: '',
      video_url: '',
      video_type: 'youtube',
      duration_minutes: 30,
      category: '',
      difficulty: 'beginner',
      skills: [],
      questions: [],
      roleplay_scenario: null,
      image_url: ''
    });
  };

  // Assignment functions
  const createAssignment = async () => {
    try {
      await api.post('/admin/assignments', newAssignment);
      toast.success('Módulo asignado correctamente');
      setShowAssignModal(false);
      setNewAssignment({
        module_id: '',
        assign_to: 'all',
        department_ids: [],
        user_ids: [],
        due_date: '',
        mandatory: true
      });
      fetchData();
    } catch (error) {
      toast.error('Error al asignar módulo');
    }
  };

  const deleteAssignment = async (assignmentId) => {
    if (!window.confirm('¿Eliminar esta asignación?')) return;
    try {
      await api.delete(`/admin/assignments/${assignmentId}`);
      toast.success('Asignación eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar asignación');
    }
  };

  // Question management
  const addQuestion = () => {
    setNewModule(prev => ({
      ...prev,
      questions: [...prev.questions, { id: `q${Date.now()}`, question: '', correct_answer: '', points: 25 }]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setNewModule(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => i === index ? { ...q, [field]: value } : q)
    }));
  };

  const removeQuestion = (index) => {
    setNewModule(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  // Roleplay management
  const initRoleplay = () => {
    setNewModule(prev => ({
      ...prev,
      roleplay_scenario: {
        title: '',
        description: '',
        learning_objective: '',
        steps: []
      }
    }));
  };

  const addRoleplayStep = () => {
    setNewModule(prev => ({
      ...prev,
      roleplay_scenario: {
        ...prev.roleplay_scenario,
        steps: [...(prev.roleplay_scenario?.steps || []), { id: `step${Date.now()}`, situation: '', expected_behavior: '' }]
      }
    }));
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 rounded-2xl bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-light">
            Panel de <span className="text-gradient font-medium">Administración</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona módulos, usuarios y asignaciones
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                <p className="text-sm text-muted-foreground">Usuarios</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-accent" />
              <div>
                <p className="text-2xl font-bold">{stats?.total_modules || 0}</p>
                <p className="text-sm text-muted-foreground">Módulos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-destructive" />
              <div>
                <p className="text-2xl font-bold">{stats?.ai_detected_answers || 0}</p>
                <p className="text-sm text-muted-foreground">IA Detectada</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{stats?.completed_assignments || 0}</p>
                <p className="text-sm text-muted-foreground">Completados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules">
            <BookOpen className="w-4 h-4 mr-2" /> Módulos
          </TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">
            <ClipboardList className="w-4 h-4 mr-2" /> Asignaciones
          </TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments">
            <Building2 className="w-4 h-4 mr-2" /> Departamentos
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" /> Usuarios
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="grid md:grid-cols-2 gap-6">
            {/* AI Detection Stats */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5" />
                  Detección de IA
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>Respuestas totales</span>
                    <Badge variant="secondary">{stats?.total_answers || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Detectadas como IA</span>
                    <Badge variant="destructive">{stats?.ai_detected_answers || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Tasa de detección</span>
                    <Badge>
                      {stats?.total_answers ? Math.round((stats.ai_detected_answers / stats.total_answers) * 100) : 0}%
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent AI Detections */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                  Respuestas IA Recientes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  {evaluations.answer_evaluations
                    .filter(e => e.is_ai_generated)
                    .slice(0, 10)
                    .map(evaluation => (
                      <div key={evaluation.id} className="p-3 mb-2 rounded-lg bg-destructive/10 border border-destructive/20">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium">Usuario: {evaluation.user_id.slice(0, 8)}...</p>
                            <p className="text-xs text-muted-foreground">
                              Probabilidad IA: {evaluation.ai_probability}%
                            </p>
                          </div>
                          <Badge variant="destructive">Rechazada</Badge>
                        </div>
                      </div>
                    ))}
                  {evaluations.answer_evaluations.filter(e => e.is_ai_generated).length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      No se han detectado respuestas de IA
                    </p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Modules Tab */}
        <TabsContent value="modules">
          <Card className="glass-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Gestión de Módulos</CardTitle>
              <Dialog open={showModuleModal} onOpenChange={setShowModuleModal}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" onClick={resetModuleForm} data-testid="new-module-btn">
                    <Plus className="w-4 h-4 mr-2" /> Nuevo Módulo
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{selectedModule ? 'Editar Módulo' : 'Crear Nuevo Módulo'}</DialogTitle>
                    <DialogDescription>
                      Configura el contenido, preguntas y juego de rol del módulo
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6 pt-4">
                    {/* Basic Info */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Título</Label>
                        <Input
                          value={newModule.title}
                          onChange={(e) => setNewModule(p => ({ ...p, title: e.target.value }))}
                          placeholder="Título del módulo"
                          data-testid="module-title"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Categoría</Label>
                        <Input
                          value={newModule.category}
                          onChange={(e) => setNewModule(p => ({ ...p, category: e.target.value }))}
                          placeholder="Ej: Soft Skills, Técnico"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea
                        value={newModule.description}
                        onChange={(e) => setNewModule(p => ({ ...p, description: e.target.value }))}
                        placeholder="Descripción breve del módulo"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Contenido (Markdown)</Label>
                      <Textarea
                        value={newModule.content}
                        onChange={(e) => setNewModule(p => ({ ...p, content: e.target.value }))}
                        placeholder="# Título&#10;&#10;Contenido del módulo..."
                        className="min-h-[200px] font-mono text-sm"
                        data-testid="module-content"
                      />
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>URL del Video</Label>
                        <Input
                          value={newModule.video_url}
                          onChange={(e) => setNewModule(p => ({ ...p, video_url: e.target.value }))}
                          placeholder="https://youtube.com/..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Duración (min)</Label>
                        <Input
                          type="number"
                          value={newModule.duration_minutes}
                          onChange={(e) => setNewModule(p => ({ ...p, duration_minutes: parseInt(e.target.value) || 30 }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Dificultad</Label>
                        <Select
                          value={newModule.difficulty}
                          onValueChange={(v) => setNewModule(p => ({ ...p, difficulty: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Principiante</SelectItem>
                            <SelectItem value="intermediate">Intermedio</SelectItem>
                            <SelectItem value="advanced">Avanzado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Questions Section */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <Label className="text-lg">Preguntas de Evaluación</Label>
                        <Button variant="outline" size="sm" onClick={addQuestion}>
                          <Plus className="w-4 h-4 mr-1" /> Agregar Pregunta
                        </Button>
                      </div>
                      {newModule.questions.map((q, idx) => (
                        <div key={q.id} className="p-4 mb-3 rounded-lg bg-secondary/50 space-y-3">
                          <div className="flex justify-between">
                            <Label>Pregunta {idx + 1}</Label>
                            <Button variant="ghost" size="sm" onClick={() => removeQuestion(idx)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                          <Textarea
                            value={q.question}
                            onChange={(e) => updateQuestion(idx, 'question', e.target.value)}
                            placeholder="¿Cuál es tu pregunta?"
                          />
                          <Textarea
                            value={q.correct_answer}
                            onChange={(e) => updateQuestion(idx, 'correct_answer', e.target.value)}
                            placeholder="Respuesta correcta/esperada"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Roleplay Section */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <Label className="text-lg flex items-center gap-2">
                          <Gamepad2 className="w-5 h-5" /> Juego de Rol
                        </Label>
                        {!newModule.roleplay_scenario ? (
                          <Button variant="outline" size="sm" onClick={initRoleplay}>
                            <Plus className="w-4 h-4 mr-1" /> Crear Roleplay
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setNewModule(p => ({ ...p, roleplay_scenario: null }))}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      
                      {newModule.roleplay_scenario && (
                        <div className="space-y-4 p-4 rounded-lg bg-accent/10 border border-accent/20">
                          <div className="space-y-2">
                            <Label>Título del Escenario</Label>
                            <Input
                              value={newModule.roleplay_scenario.title}
                              onChange={(e) => setNewModule(p => ({
                                ...p,
                                roleplay_scenario: { ...p.roleplay_scenario, title: e.target.value }
                              }))}
                              placeholder="Ej: Cliente insatisfecho"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Descripción del Escenario</Label>
                            <Textarea
                              value={newModule.roleplay_scenario.description}
                              onChange={(e) => setNewModule(p => ({
                                ...p,
                                roleplay_scenario: { ...p.roleplay_scenario, description: e.target.value }
                              }))}
                              placeholder="Describe la situación inicial..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Objetivo de Aprendizaje</Label>
                            <Input
                              value={newModule.roleplay_scenario.learning_objective}
                              onChange={(e) => setNewModule(p => ({
                                ...p,
                                roleplay_scenario: { ...p.roleplay_scenario, learning_objective: e.target.value }
                              }))}
                              placeholder="¿Qué debe demostrar el usuario?"
                            />
                          </div>
                          
                          <div className="mt-4">
                            <div className="flex justify-between items-center mb-2">
                              <Label>Pasos del Roleplay</Label>
                              <Button variant="outline" size="sm" onClick={addRoleplayStep}>
                                <Plus className="w-4 h-4 mr-1" /> Agregar Paso
                              </Button>
                            </div>
                            {newModule.roleplay_scenario.steps?.map((step, idx) => (
                              <div key={step.id} className="p-3 mb-2 rounded bg-background space-y-2">
                                <Label>Paso {idx + 1}: Situación</Label>
                                <Textarea
                                  value={step.situation}
                                  onChange={(e) => {
                                    const newSteps = [...newModule.roleplay_scenario.steps];
                                    newSteps[idx].situation = e.target.value;
                                    setNewModule(p => ({
                                      ...p,
                                      roleplay_scenario: { ...p.roleplay_scenario, steps: newSteps }
                                    }));
                                  }}
                                  placeholder="¿Qué dice o hace el cliente/interlocutor?"
                                />
                                <Label>Comportamiento Esperado</Label>
                                <Input
                                  value={step.expected_behavior}
                                  onChange={(e) => {
                                    const newSteps = [...newModule.roleplay_scenario.steps];
                                    newSteps[idx].expected_behavior = e.target.value;
                                    setNewModule(p => ({
                                      ...p,
                                      roleplay_scenario: { ...p.roleplay_scenario, steps: newSteps }
                                    }));
                                  }}
                                  placeholder="¿Cómo debería responder el usuario?"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <Button onClick={createModule} className="w-full btn-primary" data-testid="save-module-btn">
                      {selectedModule ? 'Actualizar Módulo' : 'Crear Módulo'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {modules.map(module => (
                    <div 
                      key={module.id}
                      className="p-4 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors"
                      data-testid={`module-item-${module.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium">{module.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-1">{module.description}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline">{module.category}</Badge>
                            <Badge variant="secondary">{module.duration_minutes} min</Badge>
                            <Badge>{module.questions?.length || 0} preguntas</Badge>
                            {module.roleplay_scenario && (
                              <Badge className="bg-accent/20 text-accent">
                                <Gamepad2 className="w-3 h-3 mr-1" /> Roleplay
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => editModule(module)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteModule(module.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {modules.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      No hay módulos creados
                    </p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments">
          <Card className="glass-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Asignaciones de Módulos</CardTitle>
              <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="new-assignment-btn">
                    <Send className="w-4 h-4 mr-2" /> Asignar Módulo
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Asignar Módulo a Usuarios</DialogTitle>
                    <DialogDescription>
                      Selecciona a quién asignar el módulo
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Módulo</Label>
                      <Select
                        value={newAssignment.module_id}
                        onValueChange={(v) => setNewAssignment(p => ({ ...p, module_id: v }))}
                      >
                        <SelectTrigger data-testid="select-module">
                          <SelectValue placeholder="Selecciona un módulo" />
                        </SelectTrigger>
                        <SelectContent>
                          {modules.map(m => (
                            <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Asignar a</Label>
                      <Select
                        value={newAssignment.assign_to}
                        onValueChange={(v) => setNewAssignment(p => ({ 
                          ...p, 
                          assign_to: v,
                          department_ids: [],
                          user_ids: []
                        }))}
                      >
                        <SelectTrigger data-testid="assign-to">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los usuarios</SelectItem>
                          <SelectItem value="department">Por departamento</SelectItem>
                          <SelectItem value="users">Usuarios específicos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {newAssignment.assign_to === 'department' && (
                      <div className="space-y-2">
                        <Label>Departamentos</Label>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {departments.map(dept => (
                            <div key={dept.id} className="flex items-center gap-2">
                              <Checkbox
                                checked={newAssignment.department_ids.includes(dept.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setNewAssignment(p => ({
                                      ...p,
                                      department_ids: [...p.department_ids, dept.id]
                                    }));
                                  } else {
                                    setNewAssignment(p => ({
                                      ...p,
                                      department_ids: p.department_ids.filter(d => d !== dept.id)
                                    }));
                                  }
                                }}
                              />
                              <Label className="font-normal">{dept.name}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {newAssignment.assign_to === 'users' && (
                      <div className="space-y-2">
                        <Label>Usuarios</Label>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {users.filter(u => u.role !== 'admin').map(user => (
                            <div key={user.id} className="flex items-center gap-2">
                              <Checkbox
                                checked={newAssignment.user_ids.includes(user.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setNewAssignment(p => ({
                                      ...p,
                                      user_ids: [...p.user_ids, user.id]
                                    }));
                                  } else {
                                    setNewAssignment(p => ({
                                      ...p,
                                      user_ids: p.user_ids.filter(u => u !== user.id)
                                    }));
                                  }
                                }}
                              />
                              <Label className="font-normal">{user.name} ({user.email})</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Fecha límite (opcional)</Label>
                      <Input
                        type="date"
                        value={newAssignment.due_date}
                        onChange={(e) => setNewAssignment(p => ({ ...p, due_date: e.target.value }))}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        checked={newAssignment.mandatory}
                        onCheckedChange={(c) => setNewAssignment(p => ({ ...p, mandatory: c }))}
                      />
                      <Label>Obligatorio</Label>
                    </div>

                    <Button onClick={createAssignment} className="w-full btn-primary" data-testid="create-assignment-btn">
                      Asignar Módulo
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {assignments.map(assignment => (
                    <div 
                      key={assignment.id}
                      className="p-4 rounded-xl bg-secondary/50"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium">{assignment.module_title}</h3>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline">
                              {assignment.assign_to === 'all' ? 'Todos' : 
                               assignment.assign_to === 'department' ? `${assignment.department_ids.length} departamentos` :
                               `${assignment.user_ids.length} usuarios`}
                            </Badge>
                            {assignment.mandatory && <Badge>Obligatorio</Badge>}
                            {assignment.due_date && (
                              <Badge variant="secondary">Límite: {assignment.due_date}</Badge>
                            )}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteAssignment(assignment.id)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {assignments.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      No hay asignaciones
                    </p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Departments Tab */}
        <TabsContent value="departments">
          <Card className="glass-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Departamentos</CardTitle>
              <Dialog open={showDeptModal} onOpenChange={setShowDeptModal}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="new-dept-btn">
                    <Plus className="w-4 h-4 mr-2" /> Nuevo Departamento
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Crear Departamento</DialogTitle>
                    <DialogDescription>
                      Los departamentos permiten organizar y asignar módulos a grupos de usuarios
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Nombre</Label>
                      <Input
                        value={newDept.name}
                        onChange={(e) => setNewDept(p => ({ ...p, name: e.target.value }))}
                        placeholder="Nombre del departamento"
                        data-testid="dept-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea
                        value={newDept.description}
                        onChange={(e) => setNewDept(p => ({ ...p, description: e.target.value }))}
                        placeholder="Descripción opcional"
                      />
                    </div>
                    <Button onClick={createDepartment} className="w-full btn-primary" data-testid="create-dept-btn">
                      Crear Departamento
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {departments.map(dept => (
                  <div 
                    key={dept.id}
                    className="p-4 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          {dept.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">{dept.description}</p>
                        <Badge variant="outline" className="mt-2">{dept.user_count} usuarios</Badge>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteDepartment(dept.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
                {departments.length === 0 && (
                  <p className="col-span-2 text-center text-muted-foreground py-8">
                    No hay departamentos
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Gestión de Usuarios</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {users.map(user => (
                    <div 
                      key={user.id}
                      className="p-4 rounded-xl bg-secondary/50 flex items-center justify-between"
                    >
                      <div>
                        <h3 className="font-medium">{user.name}</h3>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                            {user.role === 'admin' ? 'Admin' : 'Usuario'}
                          </Badge>
                          {user.department && (
                            <Badge variant="secondary">
                              {departments.find(d => d.id === user.department)?.name || 'Sin departamento'}
                            </Badge>
                          )}
                          <Badge variant="outline">{user.xp || 0} XP</Badge>
                        </div>
                      </div>
                      <Select
                        value={user.department || 'none'}
                        onValueChange={async (value) => {
                          try {
                            await api.put(`/admin/users/${user.id}/department`, null, {
                              params: { department_id: value === 'none' ? null : value }
                            });
                            toast.success('Departamento actualizado');
                            fetchData();
                          } catch (error) {
                            toast.error('Error al actualizar');
                          }
                        }}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Departamento" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin departamento</SelectItem>
                          {departments.map(d => (
                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPanel;
